import { LANGUAGE, type Language } from '@cmoh/shared'
import { type ReactNode, useMemo } from 'react'
import { useAuthContext } from '@/features/auth/hooks/use-auth-context'
import { useLanguage } from '@/hooks/use-translation'
import { type DateOnly, DateUtils, type IsoUtcString } from '@/lib/date-utils'
import { DateContext } from './context'

interface DateProviderProps {
  children: ReactNode
}

export function DateProvider({ children }: DateProviderProps) {
  const { user } = useAuthContext()
  const { currentLanguage } = useLanguage()

  const timezoneData = useMemo(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const savedTimezone = user?.timezone
    const userTimezone = savedTimezone || browserTimezone || 'America/Buenos_Aires'
    return { browserTimezone, savedTimezone, userTimezone }
  }, [user?.timezone])

  const dateUtils = useMemo(() => {
    const tz = timezoneData.userTimezone
    const lang = currentLanguage || user?.lang || LANGUAGE.EN
    return {
      // === Date-only ===
      buildApiDateOnly: (input: Date | string) =>
        DateUtils.buildApiDateOnly(input, tz) as IsoUtcString,
      parseApiDateOnly: DateUtils.parseApiDateOnly,
      extractDateOnly: (input: Date | string) => DateUtils.extractDateOnly(input, tz),
      utcIsoToDateOnly: DateUtils.utcIsoToDateOnly,
      dateOnlyToPickerDate: (input: DateOnly) => DateUtils.dateOnlyToPickerDate(input),
      formatDateOnly: (
        input: Date | string,
        opts?: {
          includeYear?: boolean
          pattern?: string
          intl?: Intl.DateTimeFormatOptions
        },
        locale: Language = lang,
      ) =>
        DateUtils.formatDateOnly(input instanceof Date ? input.toISOString() : input, locale, opts),

      // === Instants (depend on viewer TZ) ===
      utcToLocal: (utcIso: string) => DateUtils.utcToLocal(utcIso, tz),
      localToUtc: (localDate: Date, timeHHmm?: string) =>
        DateUtils.localToUtc(localDate, tz, timeHHmm) as IsoUtcString,
      formatInstantForDisplay: (input: Date | string, formatStr: string = 'dd/MM/yyyy HH:mm') =>
        DateUtils.formatInstantForDisplay(input, tz, formatStr),
      formatInstantLocalized: (
        input: Date | string,
        options: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        },
        locale: Language = lang,
      ) => DateUtils.formatInstantIntl(input, tz, locale, options),

      // === General ===
      nowInTimezone: (timezone: string = tz) => DateUtils.nowInTimezone(timezone),
      nowInTimezoneFormatted: (
        timezone: string,
        options: Intl.DateTimeFormatOptions = {
          hour: '2-digit',
          minute: '2-digit',
        },
        locale: Language = lang,
      ) => DateUtils.formatInstantIntl(new Date(), timezone, locale, options),
      isToday: (input: Date | string) => DateUtils.isToday(input, tz),
      getTimezoneDisplayName: (timezone: string, locale: Language = lang) =>
        DateUtils.getTimezoneDisplayName(timezone, locale),
      getTimezoneOffset: () => DateUtils.getTimezoneOffset(tz),
      getDiffWithTZ: (timezone: string) => DateUtils.getTzDiff(timezone, tz),
      getWeekNumber: (input: Date) => DateUtils.getWeekNumber(input),
    }
  }, [timezoneData.userTimezone, currentLanguage, user?.lang])

  const value = useMemo(() => ({ ...timezoneData, ...dateUtils }), [timezoneData, dateUtils])

  return <DateContext.Provider value={value}>{children}</DateContext.Provider>
}
